/**
 * <video-reviews> — a horizontal carousel of vertical review videos.
 * Clicking a card opens a full-screen <dialog> modal that plays the video with
 * sound. Inside the modal: tap the video to pause/resume, a sound on/off button,
 * and a close button. No native controls and no progress bar.
 */
if (!customElements.get('video-reviews')) {
  customElements.define(
    'video-reviews',
    class VideoReviews extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-vr-track]');
        this.cards = Array.from(this.querySelectorAll('[data-vr-card]'));
        this.mode = this.dataset.mode || 'modal';
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        // Inline mode (Video reviews - PD): each card plays in place.
        if (this.mode === 'inline') {
          this.setupInline();
          return;
        }

        this.modal = this.querySelector('[data-vr-modal]');
        this.modalVideo = this.querySelector('[data-vr-modal-video]');
        this.modalCaption = this.querySelector('[data-vr-modal-caption]');
        this.muteBtn = this.querySelector('[data-vr-mute]');
        this.autoplay = this.dataset.autoplay === 'true';

        if (!this.modal || !this.modalVideo) return;

        this.cards.forEach((card) => card.addEventListener('click', () => this.open(card)));

        this.querySelectorAll('[data-vr-close]').forEach((b) =>
          b.addEventListener('click', (e) => {
            e.stopPropagation();
            this.close();
          })
        );

        if (this.muteBtn) {
          this.muteBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.toggleMute();
          });
        }

        // Tap the video to pause / resume.
        this.modalVideo.addEventListener('click', (e) => {
          e.stopPropagation();
          this.togglePlay();
        });

        // Click on the backdrop (outside the video) closes the modal.
        this.modal.addEventListener('click', (e) => {
          if (e.target === this.modal) this.close();
        });

        // Native dialog "close" (button, Esc, or programmatic) → clean up.
        this.modal.addEventListener('close', () => this.onClosed());

        this.modalVideo.addEventListener('play', () => this.modal.removeAttribute('data-paused'));
        this.modalVideo.addEventListener('pause', () => {
          if (this.modal.open) this.modal.setAttribute('data-paused', '');
        });

        this.setupPreviews();
      }

      disconnectedCallback() {
        if (this.io) this.io.disconnect();
      }

      // ---------- Inline mode ----------
      // Paused by default (poster + centered play button). Clicking play starts the
      // video with sound and loops it; clicking the video pauses; while playing only
      // the sound on/off button shows. One card plays at a time.
      setupInline() {
        this.cards.forEach((card) => {
          const video = card.querySelector('video');
          const playBtn = card.querySelector('[data-vr-play]');
          const soundBtn = card.querySelector('[data-vr-sound]');
          if (!video) return;

          video.loop = true;
          video.muted = false;

          const setMuted = (m) => {
            video.muted = m;
            card.toggleAttribute('data-muted', m);
          };

          const playThis = () => {
            // Only one card plays at a time.
            this.cards.forEach((c) => {
              if (c === card) return;
              const v = c.querySelector('video');
              if (v && !v.paused) v.pause();
            });
            const p = video.play();
            if (p && typeof p.catch === 'function') {
              p.catch(() => {
                // Sound-on playback blocked → fall back to muted.
                setMuted(true);
                const p2 = video.play();
                if (p2 && typeof p2.catch === 'function') p2.catch(() => {});
              });
            }
          };

          const toggle = () => {
            if (video.paused) playThis();
            else video.pause();
          };

          if (playBtn) {
            playBtn.addEventListener('click', (e) => {
              e.stopPropagation();
              toggle();
            });
          }

          video.addEventListener('click', (e) => {
            e.stopPropagation();
            toggle();
          });

          if (soundBtn) {
            soundBtn.addEventListener('click', (e) => {
              e.stopPropagation();
              setMuted(!video.muted);
            });
          }

          video.addEventListener('play', () => card.toggleAttribute('data-playing', true));
          video.addEventListener('pause', () => card.toggleAttribute('data-playing', false));
          video.addEventListener('ended', () => card.toggleAttribute('data-playing', false));

          card.toggleAttribute('data-playing', false);
          card.toggleAttribute('data-muted', false);
        });
      }

      setupPreviews() {
        this.previews = this.cards.map((c) => c.querySelector('video')).filter(Boolean);
        if (!this.autoplay || this.reduceMotion || !this.previews.length) return;

        if (!('IntersectionObserver' in window)) {
          this.previews.forEach((v) => this.safePlay(v));
          return;
        }

        this.io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const v = entry.target;
              if (this.modal.open) {
                v.pause();
                return;
              }
              if (entry.isIntersecting) this.safePlay(v);
              else v.pause();
            });
          },
          { threshold: 0.4 }
        );
        this.previews.forEach((v) => this.io.observe(v));
      }

      safePlay(video) {
        const p = video.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      }

      open(card) {
        const preview = card.querySelector('video');

        // Reuse the same <source> elements as the preview for the modal video.
        this.modalVideo.innerHTML = preview ? preview.innerHTML : '';
        const poster = card.getAttribute('data-poster');
        if (poster) this.modalVideo.setAttribute('poster', poster);
        this.modalVideo.load();

        const caption = card.getAttribute('data-caption') || '';
        if (this.modalCaption) {
          this.modalCaption.textContent = caption;
          this.modalCaption.hidden = !caption;
        }

        // Pause every preview while the modal is open.
        this.previews?.forEach((v) => v.pause());

        this.modalVideo.muted = false;
        try {
          this.modalVideo.currentTime = 0;
        } catch (e) {
          /* not yet seekable */
        }

        if (typeof this.modal.showModal === 'function') this.modal.showModal();
        else this.modal.setAttribute('open', '');

        const p = this.modalVideo.play();
        if (p && typeof p.catch === 'function') {
          p.catch(() => {
            // Autoplay-with-sound blocked → fall back to muted playback.
            this.modalVideo.muted = true;
            this.updateMuteIcon();
            this.safePlay(this.modalVideo);
          });
        }
        this.updateMuteIcon();
      }

      close() {
        if (this.modal.open && typeof this.modal.close === 'function') {
          this.modal.close();
        } else {
          this.modal.removeAttribute('open');
          this.onClosed();
        }
      }

      onClosed() {
        this.modal.removeAttribute('data-paused');
        this.modalVideo.pause();
        this.modalVideo.removeAttribute('src');
        this.modalVideo.innerHTML = '';

        // Resume previews that are still in view.
        if (this.autoplay && !this.reduceMotion) {
          this.previews?.forEach((v) => {
            const r = v.getBoundingClientRect();
            if (r.top < window.innerHeight && r.bottom > 0 && r.left < window.innerWidth && r.right > 0) {
              this.safePlay(v);
            }
          });
        }
      }

      togglePlay() {
        if (this.modalVideo.paused) this.safePlay(this.modalVideo);
        else this.modalVideo.pause();
      }

      toggleMute() {
        this.modalVideo.muted = !this.modalVideo.muted;
        this.updateMuteIcon();
      }

      updateMuteIcon() {
        this.modal.toggleAttribute('data-muted', this.modalVideo.muted);
      }
    }
  );
}
